// @ts-nocheck

import React, { FC, useEffect, useState, useRef, useContext } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  GameHeader,
  ExampleImagesSection,
  TextFieldsSection,
  ImagesSection,
  MusicSection,
  FileDownloadSection,
  ActionsSection,
  ResultsSection,
  processFormData,
  GameExpiredOverlay,
} from 'src/shared/components/Constructor';
import { QRCodeGenerator } from 'src/shared/components';
import { FormValues, TextFieldConfig, ImageFieldConfig } from 'src/shared/components/Constructor/types';

import InfoIcon from '@mui/icons-material/Info';
import { Container, Box, LinearProgress, Typography, Grid, IconButton, Tooltip, Checkbox, FormControlLabel } from '@mui/material';

import { RealProgress } from '@shared/components/Constructor/GameCreationProgress';
import MusicUploader from '@shared/components/MusicUploader';
import WaveformTrimmer from '@shared/components/WaveformTrimmer';
import type { GameKey } from '@shared/types';
import { isGameExpired } from '@shared/utils/gameExpiration';
import { audioBufferToWav } from '@shared/utils/audio-utils';

import { authContext } from '@store/index';

import * as ClientActions from './client.actions';
import { getIsClientFormDisabled } from './client.utils';

import styles from './client.module.scss';

interface ClientPageProps {
  gameKey: GameKey;
}

// Конфигурация полей для ClientMaze
const textFields: TextFieldConfig[] = [
  { name: 'startGameHeader', label: 'clientMaze.textBegin', placeholder: 'clientMaze.textBeginPlaceholder', tooltip: 'clientMaze.textBeginTooltip', required: false },
  { name: 'endGameHeader', label: 'clientMaze.textFinalMemo', placeholder: 'clientMaze.textFinalMemoPlaceholder', tooltip: 'clientMaze.textFinalMemoTooltip', required: false },
  // { name: 'urlButton', label: 'clientMaze.linkUrl', placeholder: 'clientMaze.linkUrlPlaceholder', tooltip: 'clientMaze.linkTooltip', required: false },
];

const imageFields: ImageFieldConfig[] = [
  { name: 'startGameImage', label: 'clientMaze.photoOnStartScreen', aspectRatio: 1, tooltip: 'clientMaze.selectMainImageTooltip', required: false },
  { name: 'endGameImage', label: 'clientMaze.photoOnFinalScreen', aspectRatio: 1, tooltip: 'clientMaze.photoOnFinalScreenTooltip', required: false },
];

const ClientPageMaze: FC<ClientPageProps> = ({ gameKey: initialGameKey }) => {
  useEffect(() => {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
  }, []);

  const { t } = useTranslation();
  const [authData] = useContext(authContext);

  const [gameKey, setGameKey] = useState(initialGameKey);
  const { gameBuild: alreadyExistedGameBuild } = gameKey;
  const [_, setActivateGameDate] = ClientActions.useSetActivateGameDate();
  const gameDefaults = ClientActions.useFetchGameDefaults(gameKey.gameName);
  const [existingGameBuild, fetchGameBuild, gameBuildError, gameBuildLoading] = ClientActions.useFetchGameBuild(
    gameKey.gameBuildId
  );

  const [createdGameBuild, handleGameBuildSubmit, gameBuildCreationLoading] =
    ClientActions.useCreateGameBuild();
  const [trimmedAudioFile, setTrimmedAudioFile] = useState<File | null>(null);
  const [showProgress, setShowProgress] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [stepProgress, setStepProgress] = useState(0);
  const croppedPixelsDataRef = useRef<{ [key: string]: any }>({});

  const gameBuild = createdGameBuild ?? existingGameBuild ?? alreadyExistedGameBuild;
  const showGameLink = Boolean(gameBuild && gameBuild._id);

  // Проверка истекшей игры
  const [isGameExpiredState, setIsGameExpiredState] = useState(false);

  useEffect(() => {
    // Проверяем, истекла ли игра
    const expired = isGameExpired(gameKey.activateDate, gameKey.gameDayCount);
    setIsGameExpiredState(expired);
  }, [gameKey.activateDate, gameKey.gameDayCount]);

  // Определяем режим только для чтения
  const isReadOnly = authData?.isReadOnly || false;

  const {
    handleSubmit,
    formState: { errors },
    setError,
    control,
    reset,
    getValues,
    setValue,
    trigger,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      ...textFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}),
      ...imageFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}),
      customMusicFile: '',
      urlButton: '',
      fileDownloadUrl: '',
      finalScream: false,
    },
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (gameBuild) {
      const newValues: FormValues = {};
      
      // Заполняем текстовые поля
      textFields.forEach(field => {
        const keyMap: Record<string, keyof typeof gameBuild> = {
          startGameHeader: 'startGameHeader',
          endGameHeader: 'endGameHeader',
          urlButton: 'urlButton',
        };
        const buildKey = keyMap[field.name];
        if (buildKey) {
          newValues[field.name] = gameBuild[buildKey] || '';
        }
      });
      
      // Заполняем поля изображений
      imageFields.forEach(field => {
        const keyMap: Record<string, keyof typeof gameBuild> = {
          startGameImage: 'startGameImageUrl',
          endGameImage: 'endGameImageUrl',
        };
        const buildKey = keyMap[field.name];
        if (buildKey) {
          newValues[field.name] = gameBuild[buildKey] || '';
        }
      });
      
      // Дополнительные поля
      newValues.customMusicFile = gameBuild.musicUrl || gameDefaults?.defaultMusicUrl || '';
      // Разделяем: urlButton для URL, fileDownloadUrl для файла
      // Используем пустые строки вместо null для избежания предупреждений React
      newValues.urlButton = gameBuild.urlButton || '';
      newValues.fileDownloadUrl = (gameBuild as any).fileDownloadUrl || '';
      newValues.finalScream = gameBuild?.finalScream ?? false;
      
      reset(newValues);
      croppedPixelsDataRef.current = {};
      setTimeout(() => {}, 0);
    } else if (gameDefaults) {
      const newValues: FormValues = {
        ...textFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}),
        ...imageFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}),
        customMusicFile: gameDefaults.defaultMusicUrl || '',
        urlButton: '',
        fileDownloadUrl: '',
        finalScream: false,
      };

      // Устанавливаем значения по умолчанию для изображений из JSON данных
      if (gameDefaults.defaultImages) {
        newValues.startGameImage = gameDefaults.defaultImages.startGameImageUrl || '';
        newValues.endGameImage = gameDefaults.defaultImages.endGameImageUrl || '';
      }

      reset(newValues);
      setTimeout(() => {}, 0);
    }
  }, [gameBuild, reset, gameDefaults, existingGameBuild?._id]);

  // Обновляем gameKey после успешного создания игры
  useEffect(() => {
    if (createdGameBuild) {
      const result = createdGameBuild;
      if (result && result._id && result._id !== gameKey.gameBuildId) {
        setGameKey((prev) => ({
          ...prev,
          gameBuild: result,
          gameBuildId: result._id,
        }));
      }

      if (!gameKey.activateDate) {
        setActivateGameDate(gameKey._id);
        setGameKey((prev) => ({
          ...prev,
          activateDate: new Date(),
        }));
      }
    }
  }, [createdGameBuild, setActivateGameDate]);

  useEffect(() => {
    if (createdGameBuild) {
      const result = createdGameBuild;
      if (result && result._id && result._id !== gameKey.gameBuildId) {
        setGameKey((prev) => ({
          ...prev,
          gameBuild: result,
          gameBuildId: result._id,
        }));
      }

      if (!gameKey.activateDate) {
        setActivateGameDate(gameKey._id);
        setGameKey((prev) => ({
          ...prev,
          activateDate: new Date(),
        }));
      }
    }
  }, [createdGameBuild, setActivateGameDate]);

  const onSubmit = async (data: FormValues) => {
    try {
      // Показываем прогресс создания игры
      console.log('Starting progress...');
      setShowProgress(true);
      setCurrentStep('validation');
      setStepProgress(0);
      
      // Автоматическая обрезка и компрессия музыки, если она новая
      let audioFileToUse = trimmedAudioFile || data.customMusicFile;
      
      // Если есть музыкальный файл и он новый (не обрезанный), запускаем автоматическую обрезку
      if (data.customMusicFile instanceof File && !trimmedAudioFile) {
        setCurrentStep('music');
        setStepProgress(10);
        console.log('Запускаем автоматическую обрезку и компрессию музыки...');
        
        try {
          // Создаем AudioContext для обрезки
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          
          // Читаем файл
          const arrayBuffer = await data.customMusicFile.arrayBuffer();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          
          console.log('Аудио декодировано, длительность:', audioBuffer.duration, 'сек');
          
          // Используем полную длительность аудио без автоматической обрезки
          const startTime = 0;
          const endTime = audioBuffer.duration;
          
          console.log('Используем полную длительность аудио:', { startTime, endTime, duration: audioBuffer.duration });
          
          // Вычисляем сэмплы для обрезки
          const sampleRate = audioBuffer.sampleRate;
          const startSample = Math.floor(startTime * sampleRate);
          const endSample = Math.floor(endTime * sampleRate);
          const trimmedLength = endSample - startSample;
          
          // Создаем новый AudioBuffer для обрезанного аудио
          const trimmedBuffer = audioContext.createBuffer(
            audioBuffer.numberOfChannels,
            trimmedLength,
            sampleRate
          );
          
          // Копируем данные из выбранного диапазона
          for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
            const originalData = audioBuffer.getChannelData(channel);
            const trimmedData = trimmedBuffer.getChannelData(channel);
            
            for (let i = 0; i < trimmedLength; i++) {
              trimmedData[i] = originalData[startSample + i];
            }
          }
          
          // Конвертируем AudioBuffer в MP3 используя MP3Compressor
          const { MP3Compressor } = await import('@shared/utils/mp3-compression');
          const compressor = new MP3Compressor();
          
          // Сохраняем оригинальное название файла
          const originalFileName = data.customMusicFile.name;
          const fileNameWithoutExt = originalFileName.replace(/\.[^/.]+$/, '');
          
          // Создаем WAV файл из AudioBuffer
          const wavBlob = await audioBufferToWav(trimmedBuffer);
          const tempFile = new File([wavBlob], `${fileNameWithoutExt}`, { type: 'audio/wav' });
          
          const compressionResult = await compressor.compressMP3(tempFile);
          const trimmedFile = compressionResult.compressedFile;
          
          console.log('Автоматическая обрезка завершена:', trimmedFile.name, 'размер:', trimmedFile.size);
          
          audioFileToUse = trimmedFile;
          // Проверяем состояние AudioContext перед закрытием
          if (audioContext.state !== 'closed') {
            try {
              audioContext.close();
            } catch (error) {
              console.error('Ошибка при закрытии AudioContext:', error);
            }
          }
          
        } catch (error) {
          console.error('Ошибка автоматической обрезки музыки:', error);
          // Если обрезка не удалась, используем оригинальный файл
          audioFileToUse = data.customMusicFile;
        }
      }
      
      setCurrentStep('images');
      setStepProgress(30);
      
      const formData = await processFormData(
        { ...data, customMusicFile: audioFileToUse },
        gameKey,
        trimmedAudioFile,
        {
          imageFields: imageFields.map(f => f.name),
          musicFields: ['customMusicFile'],
          fileFields: ['urlButton'],
        }
      );
      
      setCurrentStep('upload');
      setStepProgress(60);
      
      await handleGameBuildSubmit(formData);
      
      setCurrentStep('creation');
      setStepProgress(80);
      
      // Имитируем завершение создания
      setTimeout(() => {
        setCurrentStep('complete');
        setStepProgress(100);
      }, 1000);
      
      // Скрываем прогресс после завершения
      setTimeout(() => {
        setShowProgress(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('root', { message: 'Ошибка при сохранении игры' });
      setShowProgress(false);
      setCurrentStep('');
      setStepProgress(0);
    }
  };
  
  const onError = (errors: any) => {
    const missingFields = [
      ...textFields.filter(f => f.required && errors[f.name]),
      ...imageFields.filter(f => f.required && errors[f.name]),
    ];

    if (missingFields.length > 0) {
      const fieldsList = missingFields.map(({ label }) => `📝 ${t(label)}`).join('\n');
      const errorMessage = `${t('client.validation.requiredFieldsMessage')}\n\n${fieldsList}`;
      setError('root', { 
        message: errorMessage
      });
    }
  };

  const handleTestGameClick = () => {
    if (gameBuild && gameBuild._id) {
      window.open(`/game?buildId=${gameBuild._id}`, '_blank');
    }
  };

  if (!gameDefaults) {
    return null;
  }

  const { activateDate, editDayCount, gameDayCount } = gameKey;

  const isClientFormDisabled = getIsClientFormDisabled(
    activateDate,
    editDayCount
  ) || isReadOnly || isGameExpiredState;

  return (
    <Container maxWidth="md" className={styles.gameContainer}>
      <GameHeader
        gameDefaults={gameDefaults}
        activateDate={activateDate ? (typeof activateDate === 'string' ? new Date(activateDate) : activateDate) : null}
        editDayCount={editDayCount}
        gameDayCount={gameDayCount}
      />

      <ExampleImagesSection gameDefaults={gameDefaults} />

      {/* Компонент прогресса создания игры */}
      {showProgress && (
        <RealProgress
          isVisible={showProgress}
          onComplete={() => {
            setShowProgress(false);
            setCurrentStep('');
            setStepProgress(0);
          }}
          currentStep={currentStep}
          stepProgress={stepProgress}
        />
      )}

      <form key={gameBuild?._id || 'default'} onSubmit={handleSubmit(onSubmit, onError)}>
        <TextFieldsSection control={control} errors={errors} fields={textFields} titleKey="clientMaze.textSectionTitle" />

        <ImagesSection 
          control={control} 
          errors={errors} 
          isReadOnly={isReadOnly} 
          cropDataRef={croppedPixelsDataRef} 
          images={imageFields}
        />

        <Box sx={{ mt: 4 }}>
          <Box className={styles.formSection}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h5" className={styles.sectionTitle}>{t('clientMaze.musicSectionTitle')}</Typography>
              <Tooltip title={t('clientAll.musicUploadTooltip')} arrow placement="top" enterTouchDelay={0} leaveTouchDelay={3000}>
                <IconButton size="small" sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="customMusicFile"
                  control={control}
                  render={({ field }) => {
                    return (
                      <MusicUploader
                        value={field.value ?? ''}
                        isDisabled={isReadOnly}
                        onChange={(file) => {
                          field.onChange(file);
                        }}
                        setError={(msg) => {
                          setError('customMusicFile', { message: msg });
                        }}
                      />
                    );
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                {(() => {
                  const customMusicFile = watch('customMusicFile');
                  const getFileName = (value: File | string | undefined) => {
                    if (!value) return '';
                    if (typeof value === 'string') {
                      return value.split('/').pop() || value;
                    }
                    return value.name;
                  };
                  
                  return (
                    <WaveformTrimmer
                      audioFile={customMusicFile instanceof File ? customMusicFile : (typeof customMusicFile === 'string' && customMusicFile ? customMusicFile : null)}
                      fileName={getFileName(customMusicFile)}
                      disabled={isReadOnly}
                      onTrimChange={(startTime: number, endTime: number) => {
                        console.log('Выбранный отрезок:', { startTime, endTime });
                      }}
                      onTrimmedFileChange={setTrimmedAudioFile}
                    />
                  );
                })()}
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Controller
                    name="finalScream"
                    control={control}
                    render={({ field }) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              disabled={isClientFormDisabled}
                              checked={field.value}
                            />
                          }
                          label={t('clientMaze.finalScream')}
                        />
                        <Tooltip title={t('clientMaze.finalScreamTooltip')} arrow placement="top" enterTouchDelay={0} leaveTouchDelay={3000}>
                          <IconButton size="small">
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <FileDownloadSection
          control={control}
          errors={errors}
          isReadOnly={isReadOnly}
          setError={setError}
          setValue={setValue}
          getValues={getValues}
          fieldName="urlButton"
        />

        <ActionsSection
          gameBuild={gameBuild}
          gameBuildCreationLoading={gameBuildCreationLoading}
          isReadOnly={isReadOnly}
          onTestGameClick={handleTestGameClick}
          hasErrors={!!errors.root || Object.keys(errors).length > 0}
        />

        <ResultsSection
          errors={errors}
          gameBuild={gameBuild}
          showGameLink={showGameLink}
        />

        {/* QR-код компонент */}
        <QRCodeGenerator
          gameUrl={gameBuild ? `${window.location.origin}/game?buildId=${gameBuild._id}` : ''}
          gameBuildId={gameBuild?._id}
          showGameLink={showGameLink}
        />
      </form>

      {/* Оверлей с уведомлением об истечении срока */}
      <GameExpiredOverlay isVisible={isGameExpiredState} />
    </Container>
  );
};

export default ClientPageMaze;